/**
 * useCockpitAction — Helper d'action mutative cockpit avec rollback 5xx.
 *
 * Pattern uniforme appliqué aux actions sensibles du cockpit Réseau :
 * publication, commentaire, statut d'intention, qualification, note privée,
 * affectation, réaffectation, consentement/refus de preuve, réponse à un avis.
 *
 * Garanties :
 * - inFlight guard par clé d'action (anti-doublon double-clic).
 * - Optimistic apply via callback `apply(prev) -> next`.
 * - Rollback EXACT via la même `apply` inverse OU snapshot complet.
 * - Toast d'erreur contextuel selon le code HTTP (5xx / 429 / 401 / 403 / autre).
 * - Le texte saisi (notes, réponses) doit être conservé par le composant
 *   appelant en gardant la state locale ; si l'appel échoue, on retourne
 *   `false` et l'appelant ne vide pas le champ.
 *
 * Utilisation :
 *   const run = useCockpitAction();
 *   const ok = await run({
 *     key: `proof-consent-${proof.id}`,
 *     optimistic: () => setState(...),
 *     rollback:   () => setState(prev),
 *     call:       () => api.post(`/proofs/${proof.id}/consent`),
 *     onSuccess:  () => toast.success('Consentement enregistré'),
 *     errorLabel: 'Le consentement n\'a pas été enregistré.',
 *   });
 *   // si ok === false, l'appelant peut décider de re-tenter, garder le draft, etc.
 */
import { useRef, useCallback } from 'react';
import { toast } from 'sonner';

const FALLBACK_MESSAGES = {
    server: "Erreur serveur. L'action n'a pas été enregistrée. Réessayez dans un instant.",
    ratelimit: 'Trop de requêtes. Patientez quelques secondes.',
    unauth: 'Connexion requise.',
    forbidden: "Vous n'avez pas l'autorisation pour cette action.",
    notfound: 'Élément introuvable.',
    network: 'Connexion réseau interrompue. Réessayez.',
    other: 'Action non enregistrée. Réessayez.',
};

function _toastForError(status, errorLabel) {
    if (!status) {
        toast.error(FALLBACK_MESSAGES.network);
        return 'network';
    }
    if (status >= 500) {
        toast.error(errorLabel ? `${errorLabel} (serveur)` : FALLBACK_MESSAGES.server);
        return 'server';
    }
    if (status === 429) { toast.error(FALLBACK_MESSAGES.ratelimit); return 'ratelimit'; }
    if (status === 401) { toast.error(FALLBACK_MESSAGES.unauth); return 'unauth'; }
    if (status === 403) { toast.error(FALLBACK_MESSAGES.forbidden); return 'forbidden'; }
    if (status === 404) { toast.error(FALLBACK_MESSAGES.notfound); return 'notfound'; }
    toast.error(errorLabel || FALLBACK_MESSAGES.other);
    return 'other';
}

export default function useCockpitAction() {
    const inFlightRef = useRef(new Set());

    return useCallback(async (opts) => {
        const {
            key,
            optimistic,
            rollback,
            call,
            onSuccess,
            errorLabel,
        } = opts;

        if (!key) throw new Error('useCockpitAction: key is required');
        if (inFlightRef.current.has(key)) {
            // double-clic / soumission concurrente — silencieux
            return false;
        }
        inFlightRef.current.add(key);

        try {
            if (typeof optimistic === 'function') optimistic();
            await call();
            if (typeof onSuccess === 'function') onSuccess();
            return true;
        } catch (e) {
            const status = e?.response?.status;
            if (typeof rollback === 'function') {
                try { rollback(); } catch { /* rollback ne doit jamais faire échouer */ }
            }
            _toastForError(status, errorLabel);
            return false;
        } finally {
            inFlightRef.current.delete(key);
        }
    }, []);
}
